/**
 * Optimized Swym Controller - Based on Working 6/10 Solution
 * Improvements: No flicker, better consent detection, production-ready
 */
(function() {
    'use strict';
  
    // Production mode - no console logs
    const isDev = window.location.hostname === 'localhost' || window.location.search.includes('debug=1');
    const log = isDev ? (...args) => console.log('🎯 Swym:', ...args) : () => {};
  
    // CSS that hides elements - removed entirely when granted  
    const hideCSS = `
      /* Hide all Swym elements when consent denied */
      a.swym-wishlist,
      span.swym-wishlist-header-counter,
      .swym-button,
      .swym-add-to-wishlist-view-product {
        display: none !important;
      }
    `;
  
    // Inject CSS immediately - will be removed when consent granted
    const style = document.createElement('style');
    style.id = 'swym-hide-css';
    style.textContent = hideCSS;
    document.head.appendChild(style);
    log('Initial CSS injected');
  
    // Set default state
    document.documentElement.setAttribute('data-swym-consent', 'denied');
  
    class SwymController {
      constructor() {
        this.currentState = 'denied';
        this.buttonEventsAttached = false;
        this.initAttempts = 0;
        this.maxAttempts = 30;
        this.hideCSS = hideCSS; // Store CSS for re-injection
      }
  
      setState(granted) {
        const newState = granted ? 'granted' : 'denied';
        if (this.currentState === newState) return;
        
        this.currentState = newState;
        document.documentElement.setAttribute('data-swym-consent', newState);
        
        if (granted) {
          // REMOVE CSS completely when granted - let original styles work
          const existingCSS = document.getElementById('swym-hide-css');
          if (existingCSS) {
            existingCSS.remove();
            log('CSS removed - original styles restored');
          }
        } else {
          // RE-INJECT CSS when denied
          if (!document.getElementById('swym-hide-css')) {
            const style = document.createElement('style');
            style.id = 'swym-hide-css';
            style.textContent = this.hideCSS;
            document.head.appendChild(style);
            log('CSS re-injected');
          }
        }
        
        log('State changed to:', newState);
      }
  
      // Improved consent detection - try multiple methods
      checkConsent() {
        // Try Shopify Privacy API first
        try {
          const cp = window.Shopify?.customerPrivacy;
          if (cp) {
            // Try userCanBeTracked (most reliable)
            if (typeof cp.userCanBeTracked === 'function') {
              const canTrack = cp.userCanBeTracked();
              log('Shopify userCanBeTracked:', canTrack);
              this.setState(canTrack);
              return true;
            }
            
            // Try getTrackingConsent (sync version)
            if (typeof cp.getTrackingConsent === 'function') {
              try {
                const result = cp.getTrackingConsent();
                if (typeof result === 'string') {
                  const granted = result === 'granted';
                  log('Shopify getTrackingConsent:', result);
                  this.setState(granted);
                  return true;
                }
              } catch (e) {
                log('getTrackingConsent not synchronous');
              }
            }
          }
        } catch (error) {
          log('Shopify API error:', error.message);
        }
  
        // Fallback to cookie parsing
        const cookie = this.parseTrackingCookie();
        if (cookie !== null) {
          log('Cookie consent:', cookie);
          this.setState(cookie);
          return true;
        }
  
        log('No consent found - defaulting to denied');
        return false;
      }
  
      // Better cookie parsing
      parseTrackingCookie() {
        // Look for Shopify's tracking consent cookie
        const match = document.cookie.match(/(?:^|;\s*)_tracking_consent=([^;]+)/);
        if (match) {
          const value = decodeURIComponent(match[1]);
          
          // Shopify uses specific values
          if (value === 'granted') return true;
          if (value.includes('not_granted') || value.includes('denied')) return false;
        }
        
        return null; // Unknown
      }
  
      // Robust button detection - attribute-based
      findButtons() {
        // Try specific Shopify selectors first
        const acceptSelectors = [
          '#shopify-pc__banner__btn-accept',
          '.shopify-pc__banner__btn-accept',
          '[data-action="accept"]',
          '[data-tracking-consent="accept"]'
        ];
        
        const declineSelectors = [
          '#shopify-pc__banner__btn-decline',
          '.shopify-pc__banner__btn-decline', 
          '[data-action="decline"]',
          '[data-tracking-consent="decline"]'
        ];
  
        let acceptBtn = null, declineBtn = null;
  
        // Find buttons using selectors
        for (const selector of acceptSelectors) {
          acceptBtn = document.querySelector(selector);
          if (acceptBtn) break;
        }
  
        for (const selector of declineSelectors) {
          declineBtn = document.querySelector(selector);
          if (declineBtn) break;
        }
  
        return { accept: acceptBtn, decline: declineBtn };
      }
  
      attachEvents() {
        if (this.buttonEventsAttached) return;
  
        const { accept, decline } = this.findButtons();
  
        if (accept) {
          log('Found Accept button');
          accept.addEventListener('click', () => {
            log('Accept clicked');
            // Small delay to allow any processing
            setTimeout(() => this.setState(true), 150);
          }, { capture: true, passive: true });
        }
  
        if (decline) {
          log('Found Decline button');
          decline.addEventListener('click', () => {
            log('Decline clicked');
            setTimeout(() => this.setState(false), 150);
          }, { capture: true, passive: true });
        }
  
        // Generic fallback for any missed buttons
        document.addEventListener('click', (e) => {
          if (e.target.tagName === 'BUTTON') {
            const text = e.target.textContent.toLowerCase().trim();
            const hasAccept = text.includes('accept') || text.includes('allow');
            const hasDecline = text.includes('decline') || text.includes('deny');
            
            if (hasAccept && !accept) {
              log('Fallback: Accept button clicked');
              setTimeout(() => this.setState(true), 150);
            } else if (hasDecline && !decline) {
              log('Fallback: Decline button clicked');
              setTimeout(() => this.setState(false), 150);
            }
          }
        }, { capture: true, passive: true });
  
        this.buttonEventsAttached = true;
        log('Button events attached');
      }
  
      // Setup mutation observer for dynamic content
      setupObserver() {
        const observer = new MutationObserver((mutations) => {
          let foundNewButtons = false;
          
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) { // Element
                // Check if it contains consent-related content
                if (node.querySelector && (
                  node.querySelector('[id*="banner"]') ||
                  node.querySelector('[class*="consent"]') ||
                  node.querySelector('[class*="privacy"]') ||
                  node.matches('[id*="banner"]') ||
                  node.matches('[class*="consent"]') ||
                  node.matches('[class*="privacy"]')
                )) {
                  foundNewButtons = true;
                }
              }
            });
          });
  
          if (foundNewButtons) {
            log('New consent elements detected');
            // Debounce re-attachment
            clearTimeout(this.reattachTimeout);
            this.reattachTimeout = setTimeout(() => {
              this.buttonEventsAttached = false;
              this.attachEvents();
            }, 100);
          }
        });
  
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
  
        log('Mutation observer setup');
      }
  
      init() {
        log('Initializing...');
        
        // Check initial consent
        this.checkConsent();
        
        // Attach button events
        this.attachEvents();
        
        // Setup observer for dynamic content
        this.setupObserver();
        
        // Keep trying to find buttons for a while
        const retryInterval = setInterval(() => {
          this.initAttempts++;
          
          if (this.initAttempts > this.maxAttempts) {
            clearInterval(retryInterval);
            log('Stopped looking for buttons');
            return;
          }
          
          // Retry button attachment if not found
          if (!this.buttonEventsAttached) {
            this.attachEvents();
          }
          
          // Retry consent check
          this.checkConsent();
          
        }, 1000);
        
        log('Initialized');
      }
    }
  
    // Initialize
    const controller = new SwymController();
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => controller.init());
    } else {
      controller.init();
    }
  
    // Debug functions (only in dev)
    if (isDev) {
      window.swymDebug = {
        grant: () => controller.setState(true),
        deny: () => controller.setState(false),
        status: () => controller.currentState,
        recheck: () => controller.checkConsent()
      };
      log('Debug functions available: swymDebug.grant/deny/status/recheck');
    }
  
  })();